import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Button, Card, Row } from 'components'
import { useLoading, useModal, useToggle, useException, useFile } from 'hooks'
import useAuth from 'hooks/useAuth'
import { UserSupplierServices, UserServices } from 'services/Supplier/Users'
import { ManuaisFornecedorService } from 'services/Generic/Generic'
import { COLORS, FORMAT, SPACING } from 'utils'
import { createArrayCopy } from 'utils/functions'
import { UsuarioFornecedor } from 'utils/types'

import { TableAcessos, ModalNewUser, ModalEditUser } from './components'
import { NovoUsuarioForm } from './components/ModalNewUser/ModalNewUser'

export type TypeHandleUser = 'edit' | 'block' | 'contract' | 'desblock'

interface IEditUsuarioForm {
  cdDocto: string
  idUsuario: number
  nmUsuario: string
  nuContato: string
  nuRamal: string
  perfilUsuarioId: number
  txEmail: string
  usuarioFornecedor: UsuarioFornecedor
}

const Users: React.FC = () => {
  const { downloadFile } = useFile()
  const { setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const { getException, createCustomException } = useException()
  const { user, usuarioFornecedor: usuario } = useAuth()
  const { push } = useHistory()
  const [openEditModal, toggleEditModal] = useToggle()
  const [isModalOpen, toggleModal] = useToggle()

  const [userList, setUserList] = useState<UsuarioFornecedor[] | undefined>()
  const [userSelected, setUser] = useState<UsuarioFornecedor | undefined>()
  const [isAdmin, setIsAdmin] = useState<boolean>(false)

  const getData = useCallback(async () => {
    setLoading('loading')

    try {
      const response = await UserSupplierServices().getUsersSupplier(
        usuario.UsuarioId
      )
      setUserList(response.data)
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: error?.message || error?.mensagem || 'Falha ao obter usuário.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }, [usuario])

  useEffect(() => {
    getData()
  }, [getData])

  const handleVerificarUsuario = async (requestData: NovoUsuarioForm) => {
    setLoading('loading')

    let objEnvio

    try {
      const cpfClient = FORMAT.unMask(requestData.cdDocto)
        .toString()
        .toLowerCase()
        .trim()

      objEnvio = {
        cdDocto: cpfClient,
        nmUsuario: requestData.nmUsuario,
        nuContato: FORMAT.unMask(requestData.nuContato),
        nuRamal: '',
        perfilUsuarioId: requestData.perfilUsuarioId,
        txEmail: requestData.txEmail,
        usuarioFornecedor: {
          flSituacao: 'P',
          usuarioIdAdministrador: user.IdUsuario === null ? 0 : user.IdUsuario
        }
      }

      const response = await UserServices().verificarUsuarioExistente({
        cdDocto: cpfClient,
        email: requestData.txEmail,
        nuContato: requestData.nuContato
      })

      if (response.status !== 200) throw new Error(response.data.title) //RETORNO 400

      if (!response.data.usuarioExistente && response.data.sucesso) {
        //CASO 4 Se o usuario não for encontrado por nenhuma das verificações ele irá retornar Sucesso = true e UsuarioExistente = False;
        handleAddNewUser(objEnvio)
        return
      }

      setLoading('idle')
      if (response.data.usuarioExistente && response.data.sucesso) {
        // CASO 1 Caso o usuario existe e seja desativo ele irá retornar Sucesso = True e UsuarioExistente = True;
        showModal({
          handleConfirm: () =>
            handleModificarFlSituacao(
              response.data.idUsuario,
              requestData.perfilUsuarioId,
              'P',
              'Usuario reativado.'
            ),
          message: response.data?.mensagem,
          type: 'success'
        })
        return
      }

      if (!response.data.sucesso) {
        //CASO 3 - Se o usuario não for encontrado pelo Email e CPF ele irá verificar pelo telefone, no qual caso exista ele irá retornar
        //Sucesso = False e Mensagem = "Usuario com telefone já Cadastrado existente"
        // CASO 2 - Caso o usuario exista e não esteja desativado ele ira retornar Sucesso = False e Mensagem = "Usuario Existente e Ativo"
        showModal({
          message: response.data?.mensagem,
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
        return
      }
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error?.response?.data?.title ||
          error?.title ||
          error?.mensagem ||
          'Erro usuario existente.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleAddNewUser = (requestData: NovoUsuarioForm) => {
    setLoading('loading')

    UserServices()
      .create(requestData)
      .then(({ data }) => {
        if (data.sucesso) {
          showModal({
            hideButtonCancel: true,
            message: 'Foi enviado para validação pelo analista',
            txtButtonConfirm: 'Fechar',
            type: 'alert'
          })

          getData()
        }
        setLoading('idle')
      })
      .catch((err) => {
        // setMsgErro(err.mensagem)
        // setLoading('error')
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: err.mensagem,
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const handleSelectUser = (user: UsuarioFornecedor, type: TypeHandleUser) => {
    setUser(user)
    if (type === 'edit') toggleEditModal()
    if (type === 'block') {
      showModal({
        handleConfirm: () => handleDisabledUser(user),
        message: 'Você realmente deseja desativar o acesso desse colaborador?',
        type: 'alert'
      })
    }
    if (type === 'contract')
      push({
        pathname: '/contratos-usuario/',
        state: {
          idUsuarioFornecedor: user.idUsuarioFornecedor,
          isValidateAcess: false
        }
      })
  }
  const handleEditUser = async (requestData: IEditUsuarioForm) => {
    setLoading('loading')
    try {
      const objEnv = {
        cdDocto: FORMAT.unMask(
          userSelected?.usuario?.cdDocto
            ? userSelected?.usuario?.cdDocto
            : requestData.cdDocto
        ),
        idUsuario: userSelected.usuario.idUsuario,
        nmUsuario: requestData.nmUsuario,
        nuContato: FORMAT.unMask(requestData.nuContato),
        nuRamal: '',
        perfilUsuarioId: requestData.perfilUsuarioId,
        txEmail: requestData.txEmail,
        usuarioFornecedor: {
          flSituacao: userSelected?.flSituacao
          // @:TROCA fornecedorId: userSelected?.fornecedorId
          //   ? userSelected?.fornecedorId
          //   : requestData?.usuarioFornecedor?.fornecedorId,
          // ...(userSelected?.idUsuarioFornecedor && {
          //   idUsuarioFornecedor: userSelected?.idUsuarioFornecedor
          // })
        }
      }
      const { data } = await UserServices().put([objEnv])

      if (!data.sucesso) {
        setLoading('idle');
        showModal({
          hideButtonCancel: true,
          message: data.mensagem,
          txtButtonCancel: "OK",
          type: 'error'
        })
        return;
      }

      showModal({
        hideButtonCancel: true,
        message: 'Usuário editado com sucesso.',
        txtButtonConfirm: 'ok',
        type: 'success'
      })
      getData()
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: error?.response?.data?.mensagem || error.message || 'Não foi possível alterar o usuário',
        txtButtonConfirm: 'OK',
        type: 'error'
      })
    }
  }

  const handleDisabledUser = (user: UsuarioFornecedor) => {
    setLoading('loading')

    const objEnv = {
      cdDocto: FORMAT.unMask(user.usuario.cdDocto),
      idUsuario: user.usuario.idUsuario,
      nmUsuario: user.usuario.nmUsuario,
      nuContato: FORMAT.unMask(user.usuario.nuContato),
      nuRamal: '',
      perfilUsuarioId: user.usuario.perfilUsuarioId,
      txEmail: user.usuario.txEmail,
      usuarioFornecedor: {
        flSituacao: 'D',
        // @:TROCA fornecedorId: user.fornecedorId,
        idUsuarioFornecedor: user.idUsuarioFornecedor
      }
    }

    UserServices()
      .put([objEnv])
      .then(({ data }) => {
        if (data.sucesso) {
          const cpyArr = createArrayCopy(userList)
          const index = cpyArr.findIndex(
            (el) => el.usuarioId === user.usuarioId
          )
          cpyArr[index].flSituacao = 'D'
          setUserList(cpyArr)
          showModal({
            hideButtonCancel: true,
            message: `Usuario "${user.usuario.nmUsuario}" desativado com sucesso!`,
            txtButtonConfirm: 'Fechar',
            type: 'success'
          })
        }
        setLoading('idle')
      })
      .catch((err) => {
        // setMsgErro(
        //   err ||
        //
        // )
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: `Não foi possivel desativar o usuario ${user.usuario.nmUsuario}`,
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }
  const checkAdmin = useCallback(() => {
    if (user?.PerfilUsuarioId === 9) {
      setIsAdmin(true)
      return
    }

    setIsAdmin(false)
  }, [user])
  useEffect(() => {
    checkAdmin()
  }, [checkAdmin])

  const handleModificarFlSituacao = async (
    idUsuario: number,
    perfilUsuarioId: number,
    flSituacao: string,
    mensagem: string
  ) => {
    setLoading('loading')
    try {
      const response = await UserServices().updateFlSituacao({
        flSituacao: flSituacao,
        idUsuario: idUsuario,
        perfilUsuarioId: perfilUsuarioId
      })

      if (response.status !== 200)
        throw new Error('Não foi possivel atualizar situacao do usuario.')
      setLoading('idle')
      showModal({
        handleConfirm: () => getData(),
        hideButtonCancel: true,
        message: mensagem,
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel atualizar situacao do usuario.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleDownloadOrientacaoUsuarioFornecedor = () => {
    setLoading('loading')

    ManuaisFornecedorService()
      .getManual('Manual-de-Operacao-do-Usuario-Fornecedor.pdf')
      .then((response) => {
        if (response.status !== 200) {
          createCustomException({
            message: 'Não foi possível fazer o download do manual'
          })
        }

        downloadFile({
          local: {
            filename: 'Manual-de-Operação-do-Usuário-Fornecedor.pdf',
            base64: response.data
          }
        })
      })
      .catch((error: Error) => {
        const { message } = getException(error)
        showModal({
          type: 'error',
          message: message || 'Houve um erro ao efetuar o download do manual',
          hideButtonCancel: true,
          txtButtonConfirm: 'OK'
        })
      })
      .finally(() => setLoading('idle'))
  }

  return (
    <>
      <Card
        marginTop={SPACING.spacing20}
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0 0 1em ${COLORS.lightGray}`}
      >
        <Row justifyContent="flex-end" marginBottom={SPACING.spacing20}>
          <Button
            text="Manual do Fornecedor"
            variantButton="outlined"
            onClick={handleDownloadOrientacaoUsuarioFornecedor}
          />
          <div style={{ width: SPACING.spacing20 }} />
          <Button
            text="ADICIONAR NOVO"
            onClick={toggleModal}
            disabled={!isAdmin}
          />
        </Row>
        <TableAcessos
          tableData={userList}
          setSelectedUser={handleSelectUser}
          isAdmin={isAdmin}
        />
      </Card>

      {!!isModalOpen && (
        <ModalNewUser
          showModal={isModalOpen}
          toggleModal={toggleModal}
          onSubmit={handleVerificarUsuario}
        />
      )}
      {!!openEditModal && (
        <ModalEditUser
          isAdmin={isAdmin}
          showModal={openEditModal}
          toggleModal={toggleEditModal}
          userData={userSelected}
          onSubmit={handleEditUser}
        />
      )}
    </>
  )
}

export default Users
